import type { Member } from '../../types/calendar';

interface MemberDotsStackProps {
  /** Array of members to display as colored dots */
  members: Member[];
  /** Maximum number of dots to show before "+N" indicator (default: 3) */
  maxVisible?: number;
  /** Dot size: 'sm' (8px) or 'md' (12px) (default: 'sm') */
  size?: 'sm' | 'md';
  /** Show member names alongside dots (default: false) */
  showNames?: boolean;
  /** Maximum number of names to show when showNames is true (default: 2) */
  maxNames?: number;
}

/**
 * Displays stacked member color dots for shared events.
 *
 * Used by:
 * - KeyEventPreview.tsx (day preview cards)
 * - UpcomingEventsCard.tsx (upcoming events sidebar)
 * - EventCard.tsx (calendar views)
 * - WeekView, MonthView in Calendar.tsx
 *
 * Reuses existing CSS classes:
 * - .member-dots-stack
 * - .member-dot with -ml-1.5 overlap and ring-2 ring-white
 */
export function MemberDotsStack({
  members,
  maxVisible = 3,
  size = 'sm',
  showNames = false,
  maxNames = 2,
}: MemberDotsStackProps) {
  if (members.length === 0) {
    return null;
  }

  // Sort members alphabetically for consistent display
  const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));
  const visibleMembers = sortedMembers.slice(0, maxVisible);
  const hiddenCount = Math.max(0, members.length - maxVisible);

  const dotSizeClass = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';

  return (
    <div className="flex items-center gap-1">
      <div className="member-dots-stack" data-testid="member-dots-stack">
        {visibleMembers.map((member) => (
          <div
            key={member.id}
            className={`member-dot ${dotSizeClass}`}
            style={{ backgroundColor: member.color }}
            title={member.name}
            data-testid={`member-dot-${member.id}`}
          />
        ))}
      </div>
      {hiddenCount > 0 && (
        <span className="text-xs text-text-muted" data-testid="hidden-count">
          +{hiddenCount}
        </span>
      )}
      {showNames && (
        <span className="text-xs text-text-secondary truncate" data-testid="member-names">
          {sortedMembers
            .slice(0, maxNames)
            .map((m) => m.name)
            .join(', ')}
          {members.length > maxNames && ` +${members.length - maxNames}`}
        </span>
      )}
    </div>
  );
}
